import {
  DashboardState,
  EMapProvider,
  EProjectAuthType,
  ESdChangeReason,
  IAndroidConfig,
  IConfig,
  IImageLibraryDto,
  IIosConfig
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import classNames from "classnames";
import ObjectHash from "object-hash";
import React, { ReactNode } from "react";
import { connect } from "react-redux";
import { ConfigService } from "../../../services/config-service";
import { ImageLibraryService } from "../../../services/image-library-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { LoadingPresentationBase, resolveLoadingPresentation } from "../../../shared/utility/first-load-skeletons";
import { applyMailSettingsIdToConfig, toMailSettingsIdValue } from "../../../shared/utility/mail-provider-config";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorIdentity } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaPageHeader from "../../ui-kit/kuika-page-header/kuika-page-header";
import { KuikaSkeletonForm, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import "./run-settings.scss";
import { ConfigAIInput } from "./app-settings-config-manager/app-settings-config-ai/app-settings-config-ai-keys-input";
import { ConfigExpireOldSessions } from "./app-settings-config-manager/app-settings-config-auth/app-settings-config-auth-expire-old-sessions";
import { ConfigTwoFa } from "./app-settings-config-manager/app-settings-config-auth/app-settings-config-auth-two-fa";
import { ConfigBucketUploadInput } from "./app-settings-config-manager/app-settings-config-bucket-upload/app-settings-config-bucket-upload-input";
import { AppSettingsRunIsProd } from "./app-settings-config-manager/app-settings-config-build-type/app-settings-run-is-prod";
import { ConfigCacheInput } from "./app-settings-config-manager/app-settings-config-cache/app-settings-config-cache-input";
import {
  ICommonConfigDeleteFunctionParams,
  ICommonConfigSaveFunctionParams
} from "./app-settings-config-manager/app-settings-config-container";
import { ConfigDateFormat } from "./app-settings-config-manager/app-settings-config-date-format/app-settings-config-date-format";
import { ConfigDocumentManagementInput } from "./app-settings-config-manager/app-settings-config-document-management/app-settings-config-document-management-input";
import { ConfigErrorMessageMode } from "./app-settings-config-manager/app-settings-config-error-handling/app-settings-config-error-message-mode";
import { FavIconField } from "./app-settings-config-manager/app-settings-config-favicon/app-settings-config-favicon-input";
import { ConfigFirebaseInput } from "./app-settings-config-manager/app-settings-config-firebase/app-settings-config-firebase-input";
import { ConfigFormAuthInput } from "./app-settings-config-manager/app-settings-config-form-authentication/app-settings-config-form-authentication-input";
import { GoogleRecaptchaInput } from "./app-settings-config-manager/app-settings-config-google-recaptcha/app-settings-config-google-recaptcha-input";
import { InversCloudBoxxInput } from "./app-settings-config-manager/app-settings-config-invers-cloud-boxx/app-settings-config-invers-cloud-boxx-input";
import { ConfigOtherLoginInputs } from "./app-settings-config-manager/app-settings-config-login-options/app-settings-config-other-login-inputs";
import { ConfigMailInput } from "./app-settings-config-manager/app-settings-config-mail/app-settings-config-mail-input";
import { ConfigAMapInput } from "./app-settings-config-manager/app-settings-config-maps/app-settings-config-amap-input";
import { ConfigGoogleMapsInput } from "./app-settings-config-manager/app-settings-config-maps/app-settings-config-google-maps-input";
import { ConfigMapProviderSelect } from "./app-settings-config-manager/app-settings-config-maps/app-settings-config-map-provider-select";
import { normalizeMapProviderDefaults } from "./app-settings-config-manager/app-settings-config-maps/map-provider-defaults";
import { ConfigAMapWebServiceInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-amap-web-service-input";
import { ConfigApifyInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-apify-input";
import { ConfigBraveSearchInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-brave-search-input";
import { ConfigDropboxInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-dropbox-input";
import { ConfigFirecrawlMcpInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-firecrawl-input";
import { ConfigGithubInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-github-input";
import { ConfigGoogleMcpInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-google-mcp-input";
import { ConfigGoogleMcpMapInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-google-mcp-map-input";
import { ConfigJiraInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-jira-input";
import { ConfigOffice365McpInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-office-365-mcp-input";
import { ConfigSlackInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-slack-input";
import { ConfigTrelloMcpInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-trello-input";
import { ConfigYoutubeMcpInput } from "./app-settings-config-manager/app-settings-config-mcp/app-settings-config-youtube-input";
import { ConfigNotifyActionDurationInput } from "./app-settings-config-manager/app-settings-config-notify-action/app-settings-config-notify-action-duration-input";
import { ConfigPasswordsInput } from "./app-settings-config-manager/app-settings-config-passwords/app-settings-config-password-input";
import { ConfigPaymentInput } from "./app-settings-config-manager/app-settings-config-payment-methods/app-settings-config-payment-input";
import { ConfigPushNotificationInput } from "./app-settings-config-manager/app-settings-config-push-notification/app-settings-config-push-notification-input";
import { ConfigSearchEngineInput } from "./app-settings-config-manager/app-settings-config-search-engine/app-settings-config-search-engine-input";
import { ConfigSessionInput } from "./app-settings-config-manager/app-settings-config-session/app-settings-config-session-input";
import { ConfigTwinInput } from "./app-settings-config-manager/app-settings-config-twin/app-settings-config-twin-input";
import { WhatsappBusinessInput } from "./app-settings-config-manager/app-settings-config-whatsapp-business/app-settings-config-whatsapp-business-input";
import { RUN_SETTINGS_CATEGORIES } from "./app-settings-config-manager/config-categories";
import {
  normalizeApiConfigErrorMessageMode,
  normalizeApiConfigSwitches,
  normalizeMapProviderChoiceSwitch,
  normalizeWebIcon
} from "./app-settings-config-manager/config-load-normalization";
import { ConfigField, ConfigSection } from "./app-settings-config-manager/config-pane-parts";
import { ConfigPaneShell } from "./app-settings-config-manager/config-pane-shell";

export interface IRunSettingsState {
  allImage?: IImageLibraryDto[];
  runConfig?: IConfig;
  /** Which category the rail is showing. Every other one stays mounted and hidden. */
  activeCategoryId: string;
  /** Where UPDATE's commit stands — see `SaveStatus`, which owns the ✓-then-close order. */
  saveStatus: KuikaButtonStatus;
}

/** Stable so the shell's memo does not see a new array on every render. */
const EMPTY_CATEGORY_IDS: string[] = [];

export interface IRunSettingsProps {
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

class RunSettings extends PureSdComponent<IRunSettingsProps & IWithSDProps, IRunSettingsState> {
  objectHash: string = "";

  formRef = React.createRef<FormInstance>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props) {
    super(props);
    this.state = {
      allImage: [],
      activeCategoryId: RUN_SETTINGS_CATEGORIES[0].id,
      saveStatus: "idle"
    };
  }

  componentDidMount() {
    this.getImageLibrary();
    this.props.sd.fetchConfigs({ shouldLockPage: true, shouldFetchGlobals: true });
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate() {
    if (this.props.nextCallback) {
      // A save the pending navigation asked for ("SAVE & CLOSE") is still running or showing its
      // ✓, and its own close runs the navigation. Asking again now would re-open the confirmation.
      if (this.saveStatus.canStart) {
        this.handleCloseConfig();
      }
      return;
    }

    if (!this.state.runConfig && !this.props.sd.isConfigPageLoading) this.loadConfig();
  }

  getObjectHash = () => {
    // `getFieldsValue(true)`, not the mounted-only `getFieldsValue()`: the "unsaved changes?"
    // prompt on close must not silently drop an edit just because its field — several of the
    // ~45 leaves this page shares with the platform panes sit behind their own conditional —
    // happens to be unmounted at the moment of asking. See the twin comment in
    // `app-settings-config-container.tsx`'s `getObjectHash`.
    const data: IConfig | undefined = this.formRef.current?.getFieldsValue(true);
    const hashObj = {
      ...(data ? normalizeMapProviderDefaults(data) : data)
    };

    return ObjectHash(hashObj);
  };

  closeConfig = () => {
    if (runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      return;
    }

    this.props.sd.setDashboardState(DashboardState.design);
  };

  handleCloseConfig = () => {
    if (this.objectHash === this.getObjectHash()) {
      this.closeConfig();
      return;
    }

    this.props.setShowModal(true);
  };

  loadConfig() {
    if (this.state.runConfig) return;
    if (this.props.sd.isConfigPageLoading) return;
    const runConfig = this.props.sd.configurations.find((conf) => conf.isRunConfig);
    const mailSettingsId: string | null = toMailSettingsIdValue(runConfig?.apiConfig);

    if (runConfig) {
      if (!runConfig.androidConfig) runConfig.androidConfig = {} as IAndroidConfig;
      if (!runConfig.iosConfig) runConfig.iosConfig = {} as IIosConfig;
      this.setState({ runConfig }, () => {
        const formValues = { ...runConfig } as any;
        formValues.apiConfig.mailSettingsId = mailSettingsId;

        // The 2F Auth / Expire Old Sessions switches and the favicon are real `Form.Item`s
        // now — see `config-load-normalization.ts` for why the default has to be applied
        // here, before the form (and therefore this component's own hash baseline) ever
        // sees the value.
        normalizeApiConfigSwitches(formValues.apiConfig);
        normalizeApiConfigErrorMessageMode(formValues.apiConfig);
        normalizeWebIcon(formValues.webConfig);
        normalizeMapProviderChoiceSwitch(formValues.webConfig);

        this.formRef.current?.setFieldsValue(formValues);
        this.objectHash = this.getObjectHash();
      });
    }
  }

  createCommonConfig = ({
    formInputName,
    customizedId,
    configKey,
    config,
    onSuccess,
    onError = () => {},
    onFinal
  }: ICommonConfigSaveFunctionParams) => {
    ConfigService.createCommonConfig(this.props.sd.project.id, configKey, config)
      .then(() => {
        onSuccess();
        this.handleUpdateCustomFormInput(formInputName, customizedId || config.id);
        this.props.sd.fetchConfigs({ shouldLockPage: true, shouldFetchGlobals: true });
        this.props.sd.fetchWhatsappBusinessTemplates();
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  updateCommonConfig = ({
    formInputName,
    customizedId,
    configKey,
    config,
    onSuccess,
    onError = () => {},
    onFinal
  }: ICommonConfigSaveFunctionParams) => {
    ConfigService.updateCommonConfig(this.props.sd.project.id, configKey, config)
      .then(() => {
        onSuccess();
        this.handleUpdateCustomFormInput(formInputName, customizedId || config.id);
        this.props.sd.fetchConfigs({ shouldLockPage: true, shouldFetchGlobals: true });
        this.props.sd.fetchWhatsappBusinessTemplates();
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  deleteCommonConfig = ({
    formInputName,
    configKey,
    configId,
    onSuccess,
    onError = () => {},
    shouldControlOtherSections,
    onFinal
  }: ICommonConfigDeleteFunctionParams) => {
    const activeConfigID = this.state.runConfig?.id;

    const isSettingUsedOnElswhere = this.props.sd.configurations.find(
      (config) =>
        config.id !== activeConfigID &&
        ((config.androidConfig && config.androidConfig[formInputName[1]] === configId) ||
          (config.iosConfig && config.iosConfig[formInputName[1]] === configId) ||
          (config.webConfig && config.webConfig[formInputName[1]] === configId))
    );

    if (isSettingUsedOnElswhere) {
      KMainFunctions.displayWarningNotification(
        "This setting is used on another configuration(s). Please discard this setting on other configurations before delete."
      );
      onFinal();
      return;
    }

    if (shouldControlOtherSections) {
      if (this.isCommonConfigUsedOnOtherSections(formInputName, shouldControlOtherSections.listOfControl)) {
        shouldControlOtherSections.onCannotDelete();
        onFinal();
        return;
      }
    }

    ConfigService.deleteCommonConfig(this.props.sd.project.id, configKey, configId)
      .then(() => {
        onSuccess();
        this.handleUpdateCustomFormInput(formInputName, null);
        this.props.sd.fetchConfigs({ shouldLockPage: true, shouldFetchGlobals: true });
        this.props.sd.fetchWhatsappBusinessTemplates();
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  handleUpdateCustomFormInput = (formItemName: string[], value: string | null) => {
    const fieldsValue = { ...this.formRef.current?.getFieldsValue() };
    fieldsValue[formItemName[0]][formItemName[1]] = value;
    this.formRef.current?.setFieldsValue(fieldsValue);
  };

  isCommonConfigUsedOnOtherSections(formInputName: string[], listOfControl: string[][]): boolean {
    const formValues: IConfig | undefined = this.formRef.current?.getFieldsValue();
    if (!formValues) return false;

    for (let i = 0; i < listOfControl.length; i++) {
      const parentDataKey = listOfControl[i][0];
      const childDataKey = listOfControl[i][1];

      if (!formValues[parentDataKey]) continue;
      if (parentDataKey === formInputName[0] && childDataKey === formInputName[1]) continue;

      if (formValues[parentDataKey][childDataKey] === formValues[formInputName[0]][formInputName[1]]) return true;
    }

    return false;
  }

  onSubmit = (formValues) => {
    if (!this.state.runConfig) return;
    // UPDATE is live again the moment its ✓ replaces the spinner, so a second Enter would re-fire
    // the save behind the closing page.
    if (!this.saveStatus.canStart) return;

    const { runConfig } = this.state;

    // The favicon is a real `Form.Item` now — `formValues.webConfig.appIcon` — rather than
    // container state, so this validates what is actually about to be saved.
    const webAppIcon: string | null = formValues.webConfig?.appIcon ?? null;
    if (webAppIcon) {
      if (!/^[^.]+\.(png|jpe?g|ico)$/i.test(webAppIcon)) {
        KMainFunctions.displayWarningNotification(
          "Invalid favicon file format. Please select an appropriate favicon file or deselect the current file."
        );
        return;
      }
    }
    const {
      commonSettings: { genericAuthSettings, oAuth2Settings, ldapSettings }
    } = this.props.sd;
    const config: IConfig = {
      ...runConfig,
      ...formValues
    };

    applyMailSettingsIdToConfig(config.apiConfig, formValues.apiConfig.mailSettingsId);
    formValues.apiConfig.authProviderType = EProjectAuthType.none;
    if (formValues.apiConfig.authProviderSettingsId) {
      if (genericAuthSettings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.genericAuth;
      } else if (oAuth2Settings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.oAuth2;
      } else if (ldapSettings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.ldap;
      }
    }
    // `isTwoFactorAuthenticationEnabled` / `isExpireOldSessionsEnabled` are real `Form.Item`s
    // now, already carried by `...formValues` above — only the duration needs parsing.
    config.apiConfig.twoFactorAuthenticationDayLimit =
      parseInt(config.apiConfig.twoFactorAuthenticationDayLimit as any) || 0;

    // The favicon is likewise already correct on `config` via `...formValues` above.
    config.webConfig.title = config.webConfig.title?.trim() ?? null;

    this.saveStatus.start();
    ConfigService.updateConfig(this.props.sd.project.id, config)
      .then((_response) => {
        // What is on screen is what was saved: a Back pressed while the ✓ is up has nothing to ask about.
        this.objectHash = this.getObjectHash();
        // A close confirmation this save answered ("SAVE & CLOSE") steps aside at once, so the ✓
        // on UPDATE is what is seen before the page leaves.
        this.props.setShowModal(false);
        // Refetched while the ✓ is up, and not behind the page lock: the page is leaving, and the
        // lock would draw over the ✓ it is waiting on.
        this.props.sd.fetchConfigs({ shouldLockPage: false, shouldFetchGlobals: true });
        this.props.sd.fetchWhatsappBusinessTemplates();
        KMainFunctions.displaySuccessNotification("Run settings has been saved");
        this.saveStatus.succeed(this.closeConfig);
      })
      .catch((error) => {
        this.saveStatus.fail();
        KMainFunctions.exceptionHandler(error);
      });
  };

  customFormInputValueUpdater = (data: object) => {
    this.formRef.current?.setFieldsValue({
      ...this.formRef.current?.getFieldsValue(),
      ...data
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  getSectionDescription = (contentKey: string): ReactNode => {
    const { configLocalizations } = this.props.sd;

    const content = configLocalizations.find((localization) => localization.key === contentKey);
    if (!content) return undefined;

    const isMainAi = KMainFunctions.isMainAI();

    return (
      <>
        {content.description}
        {!!content.helpLink && !isMainAi && (
          <a href={content.helpLink} target="_blank" rel="noreferrer">
            {" "}
            Learn more
          </a>
        )}
      </>
    );
  };

  render() {
    // `isConfigPageLoading` is raised by the first load AND by every settings dialog's save, which
    // refetches the configurations while this page stays open. Having the run configuration is
    // what tells them apart: without it the page has nothing to show and draws the form's outline;
    // with it the settings stay on screen and only hold still until the list is back. Neither is
    // the full-screen overlay this used to be, which also covered the dialog whose ✓ was showing.
    const presentation: LoadingPresentationBase = resolveLoadingPresentation(
      this.props.sd.isConfigPageLoading,
      this.state.runConfig !== undefined
    );
    const isFirstLoad: boolean = presentation === "skeleton";
    const isRefreshing: boolean = presentation === "refreshing";

    return (
      <div className="kuika-run-settings">
        <div className="kuika_app-settings">
          <div className="kuika_app-settings__content">
            <Form className="kuika-run-settings__form" ref={this.formRef} onFinish={this.onSubmit}>
              <KuikaPageHeader
                variant="bar"
                title="Run Settings"
                onBack={this.handleCloseConfig}
                backLabel="Back to the designer"
                // One per project, so the name is an identity with nothing to switch to.
                renderTitle={(title: string): React.ReactNode => (
                  <KuikaEditorIdentity
                    icon={
                      <span className="kuika-run-settings__glyph">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M17.6829 5H7.14634C5.96312 5 5 5.96312 5 7.14634V7.92683C5 8.24995 5.26224 8.51219 5.58537 8.51219H19.2439C19.567 8.51219 19.8293 8.24995 19.8293 7.92683V7.14634C19.8293 5.96312 18.8661 5 17.6829 5ZM7.14634 7.34146C6.82322 7.34146 6.56098 7.07922 6.56098 6.7561C6.56098 6.43298 6.82322 6.17073 7.14634 6.17073C7.46946 6.17073 7.73171 6.43298 7.73171 6.7561C7.73171 7.07922 7.46946 7.34146 7.14634 7.34146ZM9.09756 7.34146C8.77444 7.34146 8.5122 7.07922 8.5122 6.7561C8.5122 6.43298 8.77444 6.17073 9.09756 6.17073C9.42068 6.17073 9.68293 6.43298 9.68293 6.7561C9.68293 7.07922 9.42068 7.34146 9.09756 7.34146Z"
                            fill="currentColor"
                          ></path>
                          <path
                            d="M20.6507 15.1924L21 14.8564C20.8435 14.4088 20.6172 13.9959 20.3342 13.6314L19.8644 13.7703C19.0078 14.0236 18.1141 13.5077 17.9046 12.6394L17.77 12.0806C17.551 12.0435 17.3266 12.0244 17.0976 12.0244C16.8685 12.0244 16.6441 12.0435 16.4252 12.0806L16.2905 12.6394C16.081 13.5077 15.1873 14.0236 14.3307 13.7703L13.8609 13.6314C13.5783 13.9955 13.3516 14.4088 13.1951 14.8564L13.5444 15.1924C14.1824 15.8066 14.1824 16.8279 13.5444 17.4421L13.1951 17.7781C13.3516 18.2258 13.578 18.6386 13.8609 19.0031L14.3307 18.8642C15.1873 18.6109 16.081 19.1268 16.2905 19.9951L16.4252 20.554C16.6441 20.5906 16.8689 20.6098 17.0976 20.6098C17.3266 20.6098 17.551 20.5906 17.77 20.5536L17.9046 19.9947C18.1141 19.1264 19.0078 18.6105 19.8644 18.8638L20.3342 19.0027C20.6168 18.6386 20.8435 18.2254 21 17.7778L20.6507 17.4418C20.0127 16.8279 20.0127 15.8062 20.6507 15.1924ZM17.0976 17.4878C16.4509 17.4878 15.9268 16.9637 15.9268 16.3171C15.9268 15.6704 16.4509 15.1463 17.0976 15.1463C17.7442 15.1463 18.2683 15.6704 18.2683 16.3171C18.2683 16.9637 17.7442 17.4878 17.0976 17.4878Z"
                            fill="currentColor"
                          ></path>
                          <path
                            d="M12.2718 17.878H7.14634C6.6082 17.878 6.17073 17.4402 6.17073 16.9024V7.14634C6.17073 6.60859 6.6082 6.17073 7.14634 6.17073H17.6829C18.2211 6.17073 18.6585 6.60859 18.6585 7.14634V11.4913C19.0753 11.626 19.4675 11.8148 19.8293 12.047V7.14634C19.8293 5.96273 18.8661 5 17.6829 5H7.14634C5.96312 5 5 5.96273 5 7.14634V16.9024C5 18.086 5.96312 19.0488 7.14634 19.0488H12.8271C12.5953 18.687 12.4064 18.2948 12.2718 17.878Z"
                            fill="currentColor"
                          ></path>
                        </svg>
                      </span>
                    }
                    name={title}
                  />
                )}
                actions={
                  <KuikaButton
                    type="primary"
                    size="md"
                    uppercase
                    htmlType="submit"
                    status={this.state.saveStatus}
                    disabled={this.props.sd.isGeneratingPreview}
                  >
                    Update
                  </KuikaButton>
                }
              />
              {isFirstLoad && (
                <KuikaSkeletonRegion label="Loading run settings" className="kuika-run-settings__skeleton">
                  <KuikaSkeletonForm fields={6} />
                </KuikaSkeletonRegion>
              )}
              {/* Hidden rather than unmounted during the first load: the form's fields are what
                  `loadConfig` writes the run configuration into the moment it arrives. */}
              <div
                className={classNames(
                  "kuika-run-settings__body",
                  "kuika_app-settings__content__container__config-manager",
                  { "kuika-run-settings__body--busy": isRefreshing }
                )}
                hidden={isFirstLoad}
                aria-busy={isRefreshing || undefined}
              >
                <ConfigPaneShell
                  categories={RUN_SETTINGS_CATEGORIES}
                  activeCategoryId={this.state.activeCategoryId}
                  onSelectCategory={(activeCategoryId: string): void => this.setState({ activeCategoryId })}
                  // Run Settings has no parent configuration to differ from, so the rail
                  // draws neither override rings nor change dots.
                  overriddenCategoryIds={EMPTY_CATEGORY_IDS}
                  aside={
                    <>
                      <ConfigSection rail title="Title">
                        <ConfigField aside tooltipContentKey="web_title_title">
                          <Form.Item name={["webConfig", "title"]}>
                            <KuikaInput size="md" />
                          </Form.Item>
                        </ConfigField>
                      </ConfigSection>
                      <ConfigSection rail title="Favicon" description="32x32 px and JPG, JPEG, PNG or ICO file">
                        <Form.Item name={["webConfig", "appIcon"]} noStyle>
                          <FavIconField
                            acceptTpye=".jpg,.jpeg,.png,.ico"
                            allImage={this.state.allImage}
                            handleGetImageLibrary={() => {
                              this.getImageLibrary();
                            }}
                          />
                        </Form.Item>
                      </ConfigSection>
                    </>
                  }
                >
                  <ConfigSection
                    categoryId="sessions"
                    active={this.state.activeCategoryId === "sessions"}
                    title="Sessions"
                    description={this.getSectionDescription("web_sessions_description")}
                  >
                    <ConfigSessionInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                      handleUpdateCustomFormInput={() => {}}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="webMaps"
                    active={this.state.activeCategoryId === "webMaps"}
                    title="Maps"
                    description={this.getSectionDescription("web_googlemaps_description")}
                  >
                    <ConfigMapProviderSelect
                      parentData="webConfig"
                      tooltipContentKey="web_maps_provider_info"
                      supportsEndUserChoice
                      renderProvider={(provider: EMapProvider) =>
                        provider === EMapProvider.Amap ? (
                          <ConfigAMapInput
                            parentData="webConfig"
                            commonSettings={this.props.sd.commonSettings}
                            getSectionDescription={this.getSectionDescription}
                            handleCreateCommonConfig={this.createCommonConfig}
                            handleUpdateCommonConfig={this.updateCommonConfig}
                            handleDeleteCommonConfig={this.deleteCommonConfig}
                            handleUpdateCustomFormInput={() => {}}
                          />
                        ) : (
                          <ConfigGoogleMapsInput
                            parentData="webConfig"
                            commonSettings={this.props.sd.commonSettings}
                            getSectionDescription={this.getSectionDescription}
                            handleCreateCommonConfig={this.createCommonConfig}
                            handleUpdateCommonConfig={this.updateCommonConfig}
                            handleDeleteCommonConfig={this.deleteCommonConfig}
                            handleUpdateCustomFormInput={() => {}}
                          />
                        )
                      }
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="authentication"
                    active={this.state.activeCategoryId === "authentication"}
                    title="Authentication & Authorization"
                    description={this.getSectionDescription("appsettings_authentication_authorization_description")}
                  >
                    <ConfigFormAuthInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                      isRunConfig
                      handleUpdateCustomFormInput={() => {}}
                    />
                    <ConfigPasswordsInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                      handleUpdateCustomFormInput={() => {}}
                    />
                    <ConfigOtherLoginInputs
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                      handleUpdateCustomFormInput={() => {}}
                    />
                    <ConfigTwoFa />
                    <ConfigExpireOldSessions />
                  </ConfigSection>

                  <ConfigSection
                    categoryId="cacheSettings"
                    active={this.state.activeCategoryId === "cacheSettings"}
                    title="Cache Settings"
                    description={this.getSectionDescription("appsettings_cachesettings_description")}
                  >
                    <ConfigCacheInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>

                  <ConfigSection
                    categoryId="notifyActions"
                    active={this.state.activeCategoryId === "notifyActions"}
                    title="Notify Actions"
                    description={this.getSectionDescription("")}
                  >
                    <ConfigNotifyActionDurationInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>

                  <ConfigSection
                    categoryId="errorMessages"
                    active={this.state.activeCategoryId === "errorMessages"}
                    title="Error Messages"
                    description={this.getSectionDescription("appsettings_errormessages_description")}
                  >
                    <ConfigErrorMessageMode parentData="apiConfig" />
                  </ConfigSection>

                  <ConfigSection
                    categoryId="mail"
                    active={this.state.activeCategoryId === "mail"}
                    title="Mail"
                    description={this.getSectionDescription("appsettings_mail_description")}
                  >
                    <ConfigMailInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                      handleUpdateCustomFormInput={function (formItemName: string[], value: string | null): void {
                        throw new Error("Function not implemented.");
                      }}
                    />
                  </ConfigSection>
                  {!KMainFunctions.isMainAI() && (
                    <ConfigSection
                      categoryId="ai"
                      active={this.state.activeCategoryId === "ai"}
                      title="AI"
                      description={this.getSectionDescription("appsettings_ai_description")}
                    >
                      <ConfigAIInput
                        commonSettings={this.props.sd.commonSettings}
                        getSectionDescription={this.getSectionDescription}
                        handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                        handleCreateCommonConfig={this.createCommonConfig}
                        handleUpdateCommonConfig={this.updateCommonConfig}
                        handleDeleteCommonConfig={this.deleteCommonConfig}
                      />
                    </ConfigSection>
                  )}

                  <ConfigSection
                    categoryId="bucketUpload"
                    active={this.state.activeCategoryId === "bucketUpload"}
                    title="Bucket Upload"
                    description={this.getSectionDescription("")}
                  >
                    <ConfigBucketUploadInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="documentManagement"
                    active={this.state.activeCategoryId === "documentManagement"}
                    title="Document Management"
                    description={this.getSectionDescription("appsettings_documentmanagement_description")}
                  >
                    <ConfigDocumentManagementInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="fullTextSearchEngine"
                    active={this.state.activeCategoryId === "fullTextSearchEngine"}
                    title="Full-Text Search Engine"
                    description={this.getSectionDescription("appsettings_searchengine_description")}
                  >
                    <ConfigSearchEngineInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="paymentMethod"
                    active={this.state.activeCategoryId === "paymentMethod"}
                    title="Payment Method"
                    description={this.getSectionDescription("appsettings_paymentmethod_description")}
                  >
                    <ConfigPaymentInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                      handleUpdateCustomFormInput={function (formItemName: string[], value: string | null): void {
                        throw new Error("Function not implemented.");
                      }}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="inversCloudBoxx"
                    active={this.state.activeCategoryId === "inversCloudBoxx"}
                    title="Invers CloudBoxx"
                    description={this.getSectionDescription("appsettings_inverscloudboxx_description")}
                  >
                    <InversCloudBoxxInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                      handleUpdateCustomFormInput={function (formItemName: string[], value: string | null): void {
                        throw new Error("Function not implemented.");
                      }}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="whatsappBusiness"
                    active={this.state.activeCategoryId === "whatsappBusiness"}
                    title="WhatsApp Business"
                    description={this.getSectionDescription("appsettings_recaptcha_description")}
                  >
                    <WhatsappBusinessInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="googleRecaptcha"
                    active={this.state.activeCategoryId === "googleRecaptcha"}
                    title="Google reCAPTCHA"
                    description={this.getSectionDescription("appsettings_recaptcha_description")}
                  >
                    <GoogleRecaptchaInput
                      // parentConfigValue={parentChildDifferences?.googleRecaptchaSettingsId}
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="webPushNotification"
                    active={this.state.activeCategoryId === "webPushNotification"}
                    title="Push Notification"
                    description={this.getSectionDescription("android_pushnotification_description")}
                  >
                    <ConfigPushNotificationInput
                      parentData="webConfig"
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="androidPushNotification"
                    active={this.state.activeCategoryId === "androidPushNotification"}
                    title="Android Push Notification"
                    description={this.getSectionDescription("android_pushnotification_description")}
                  >
                    <ConfigFirebaseInput
                      parentData="androidConfig"
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="iosPushNotification"
                    active={this.state.activeCategoryId === "iosPushNotification"}
                    title="iOS Push Notification"
                    description={this.getSectionDescription("ios_pushnotification_description")}
                  >
                    <ConfigPushNotificationInput
                      parentData="iosConfig"
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="buildType"
                    active={this.state.activeCategoryId === "buildType"}
                    title="Build Type"
                  >
                    <AppSettingsRunIsProd />
                  </ConfigSection>
                  <ConfigSection
                    categoryId="dateTimeFormats"
                    active={this.state.activeCategoryId === "dateTimeFormats"}
                    title="Date & Time Formats"
                    description={this.getSectionDescription("date_time_formats_description")}
                  >
                    <ConfigDateFormat />
                  </ConfigSection>

                  <ConfigSection
                    categoryId="mcp"
                    active={this.state.activeCategoryId === "mcp"}
                    title="MCP"
                    description={this.getSectionDescription("appsettings_mcp_description")}
                  >
                    <ConfigBraveSearchInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigSlackInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigDropboxInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigGithubInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigGoogleMcpInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigMapProviderSelect
                      parentData="apiConfig"
                      label="Maps provider"
                      tooltipContentKey="appsettings_mcp_map_provider"
                      renderProvider={(provider: EMapProvider) =>
                        provider === EMapProvider.Amap ? (
                          <ConfigAMapWebServiceInput
                            commonSettings={this.props.sd.commonSettings}
                            getSectionDescription={this.getSectionDescription}
                            handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                            handleCreateCommonConfig={this.createCommonConfig}
                            handleUpdateCommonConfig={this.updateCommonConfig}
                            handleDeleteCommonConfig={this.deleteCommonConfig}
                          />
                        ) : (
                          <ConfigGoogleMcpMapInput
                            commonSettings={this.props.sd.commonSettings}
                            getSectionDescription={this.getSectionDescription}
                            handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                            handleCreateCommonConfig={this.createCommonConfig}
                            handleUpdateCommonConfig={this.updateCommonConfig}
                            handleDeleteCommonConfig={this.deleteCommonConfig}
                          />
                        )
                      }
                    />

                    <ConfigJiraInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigYoutubeMcpInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigTrelloMcpInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigOffice365McpInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigFirecrawlMcpInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />

                    <ConfigApifyInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleUpdateCustomFormInput={this.handleUpdateCustomFormInput}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                  {/* It had no heading of its own in the old scroll — it simply followed
                      MCP's last row, which is why it read as one of MCP's settings. */}
                  <ConfigSection
                    categoryId="kuikagentsIntegration"
                    active={this.state.activeCategoryId === "kuikagentsIntegration"}
                    title="KuikAgents Integration"
                  >
                    <ConfigTwinInput
                      commonSettings={this.props.sd.commonSettings}
                      getSectionDescription={this.getSectionDescription}
                      handleCreateCommonConfig={this.createCommonConfig}
                      handleUpdateCommonConfig={this.updateCommonConfig}
                      handleDeleteCommonConfig={this.deleteCommonConfig}
                    />
                  </ConfigSection>
                </ConfigPaneShell>
              </div>
            </Form>
          </div>
        </div>
        <CloseConfirmationModal
          itemName="Run Settings"
          closeType="Configuration"
          triggerModal={this.props.showModal}
          // "SAVE & CLOSE" runs the same save; the dialog says it is busy until the save answers.
          isLoading={this.state.saveStatus === "loading"}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeConfig();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const runSettings = connect(mapStateToProps, mapDispatchToProps)(withSD<IRunSettingsProps>(RunSettings));
export { runSettings as RunSettings };
